(ns web3.screens.content
  (:require [reagent.core :as r]
            [web3.components.card :as card]
            [web3.components.icons :as icons]
            [web3.components.widgets :as widgets]
            [web3.forms.fields :as fields]
            [web3.persistence.deletions :as deletions]
            [web3.persistence.graphql :as g]
            [web3.queries.asset :as q-asset]
            [web3.i18n :as i]
            [web3.routing.core :as routing]
            [web3.view :as v]
            [web3.log :as log]
            [web3.utils :as utils :include-macros true]
            [web3.components.parent :as parent]
            [web3.tracking.components :as tc]
            [web3.routing.utils :as ru]
            [web3.routers.app-helpers :as app-route]
            [clojure.string :as str]
            [web3.icons.shapes :as shapes]
            [web3.view.hiccup :as hiccup]
            [web3.components.linkify :as linkify]
            [web3.meta.asset :as am]))

;; ========== Components ==========

(defn content-icon
  [{:keys [mediaType category]}]
  (-> (case category
        "video" :files/video
        "image" :files/photos
        "note" :files/notes
        (get {"video/mp4" :files/video
              "application/pdf" :files/pdf
              "text/plain" :files/documents} mediaType :files/documents))
      (icons/get)
      (hiccup/size 35)))

(defn content-alt
  "String for alt tag on content image"
  [content]
  (str "Photo of " (or (:title content) (:name content))))

;; ========== Preview ==========

(defn image-preview
  [{:keys [link imageLink] :as content}]
  [:a.relative.inline-block {:target "_blank"
                             :rel "noopener"
                             ;; TODO
                             ;; lightbox view to zoom image
                             :href link}
   [:img {:alt (content-alt content)
          :src (or imageLink link)
          :style {:max-width "100%"
                  :max-height 500}}]
   [:div.absolute.pad-all-2.bottom-0.right-0
    ;; NOTE
    ;; may want to use a 'download' icon for some content types.
    widgets/zoom-icon]])

(defn doc-download [{:keys [link imageLink] :as content}]
  (if imageLink
    (image-preview content)
    [:a.flex.items-center.justify-center.pad-all-4.text-black.width-100p.bg-off-white.hover-opacity-parent
     {:href link
      :target "_blank"
      :rel "noopener"}
     (or (when imageLink
           [:img {:alt (content-alt content)
                  :src imageLink
                  :height 80}])
         (-> (content-icon content)
             (hiccup/class "hover-opacity-child")
             (hiccup/size 80)))]))

(def video-preview doc-download)
(def document-preview doc-download)

(defn content-preview
  [{:keys [category link mediaType] :as content}]
  (case category
    "receipt"
    (if (= mediaType "application/pdf")
      (document-preview content)
      (image-preview content))
    ("image" "nameplate")
    (image-preview content)
    "document"
    (if-not (= mediaType "application/pdf")
      (image-preview content)
      (document-preview content))
    "note"
    nil                                                     ;; displayed in description
    "video"
    (video-preview content)
    (do
      (log/prn :warn :unknown-category category content)
      category)))

;; ========== Details ==========

(defn detail-group
  [{:keys [header title]}]
  [:div
   [:div.text-category
    header]
   [:div.header-4.margin-top-1.truncate
    title]])

(def fields-path
  [:data])

(def retailer-names
  {"OfficeDepot" "Office Depot"
   "GeneralElectric" "General Electric"})

(v/defview product-details
  [{:as product
    :keys [description name retailLinks]
    {image-link :link} :primaryImage}]
  [:div.pad-h-3
   [:div.header-3.margin-v-3 name]
   [:div.flex.flex-wrap
    (when image-link
      [:div.pad-right-3.margin-h-auto
       [:img {:alt (str "Image of " name)
              :style {:max-width 250}
              :src image-link}]])
    [:div.flex-auto
     [:div.weight-semi-bold.text-size-13.margin-v-3 (i/get :i/product-info)]
     [:div.pre-wrap.margin-v-3 description]
     (when-let [{:as retail-link
                 :keys [link retailer]} (first retailLinks)]
       [tc/a
        {:class "round-3 bg-black text-white pad-v-3 pad-h-4 inline-block text-uppercase"
         :href link
         :target "_blank"
         :rel "noopener"}
        (str "Online at " (get retailer-names retailer retailer))])]]])

(defn delete-content [{:as content}]
  (deletions/delete-with-confirm
    (merge content
           {:text-primary (i/get :i/delete-type.primary {:type (str/lower-case (i/get (keyword "i" (:category content))))})
            :mutate! (comp (partial q-asset/content-delete! {:id (:id content)})
                           #(assoc % :refetch [q-asset/entity-content-recursive
                                               q-asset/content]))
            :on-complete (partial deletions/close-deleted-entity-modal! (:id content))
            :snack (i/get :content/delete-complete)})))

(v/defview content-details
  {:get-initial-state
   (fn [this]
     (let [{:keys [title description]} (r/props this)]
       (assoc-in {} fields-path {:Content/title title
                                 :Content/description description})))}
  [{:keys [id title description category parent createdAt curated
           link imageLink mediaType editable?]
    :or {editable? true}
    :as content}]
  (let [{:keys [state] :as this} (r/current-component)
        kedit [:edit :content]
        editable? (and (not curated)
                       (not (:sample parent))
                       editable?)
        edit? (and editable? (routing/get-query kedit))
        content-mutation! (fn [path this value opts]
                            (let [data (-> content
                                           ;; we get `parent` from query, but
                                           ;; submit `contentedId` in mutations
                                           (assoc :contentedId (:id parent))
                                           (assoc-in path value))]
                              (q-asset/content-update! data (merge opts
                                                                   {:component this
                                                              :refetch [q-asset/asset-group*
                                                                        q-asset/asset-contents
                                                                        q-asset/entity-content-recursive]}))))
        set-parent! (fn [value]
                      (let [mutate! (partial content-mutation! [:contentedId])]
                        (mutate! this value)

                        ;; I don't think we should navigate away from the current item
                        ;; when we change its parent
                        #_(go
                            (<! (timeout 0))
                            (->> {:id value
                                  :__typename (g/local-typename value)}
                                 app-route/parent-href
                                 routing/nav!))))
        content-row :div.margin-bottom-3.relative.flex.justify-center
        note? (= "note" category)
        input-props (fn [data-key]
                      {:class "width-100p"
                       :container {:class "width-100p"}
                       :read-only? (not edit?)
                       :persist-value! (partial content-mutation! [data-key])
                       :persisted-value (get-in content [data-key])})]
    [:div.pad-h-3.pad-top-3
     (when-not note?
       [content-row
        (content-preview content)])
     [widgets/graphql-state (g/mutation-state)]
     [content-row
      [:div.header-3.flex.flex-auto
       (if edit?
         [fields/from-schema :Content/title
          (assoc (input-props :title)
            :dense? true)]
         title)]
      [:div.inline-flex
       (when editable?
         (widgets/edit-pencil
           {:on-click #(routing/set-query! kedit (when-not edit? true))
            :class (when edit? "icon--active")}))]]
     [content-row
      [:div.flex.flex-auto
       (when parent
         (if edit?
           [fields/from-schema :Content/contentedId
            {:value (:id parent)
             :change-value! set-parent!
             :select-asset? true}]
           [:a.hover-text-real-black.block.flex-auto
            {:href (app-route/parent-href parent)}
            (detail-group
              {:header (parent/parent-type (:id parent))
               :title (parent/parent-name (:id parent))})]))]
      [:div.flex
       (detail-group
         {:header (i/get :i/date)
          :title (utils/format-date createdAt)})]]
     [content-row
      [:div.text-body.width-100p.pre-wrap
       (let [description-edit (if note?
                                ;; the "description" for a note is actually a :Content/body field
                                ;; stored under the :link key
                                [fields/from-schema :Content/body
                                 (input-props :link)]
                                [fields/from-schema :Content/description
                                 (input-props :description)])
             description-view (if note? (some-> link
                                                (utils/link-data-decode)
                                                (linkify/linkify))
                                        description)]
         (if edit?
           description-edit
           description-view))]]

     (when (and editable? edit?)
       [:div.margin-top-n3
        [widgets/primary-button
         {:class "border-gray"
          :on-click (partial delete-content content)}
         (i/get :i/delete-file)]
        [widgets/black-button
         {:class "margin-v-3"
          :on-click #(routing/set-query! kedit nil)}
         (i/get :i/done)]])]))

;; ========== Views ==========

(defn note-label
  [{:keys [title]
    :as content}]
  [:div.text-left.pad-all-2.width-100p
   [:div.truncate.nowrap.text-size-16
    title]
   [:div.text-dark-gray.text-size-14.line-h-17.truncate-multiline-4.margin-top-1
    (utils/link-data-decode (:link content))]])

(defn content-card
  [{:keys [imageLink thumbnailLink title curated large? notes-grid? edit? legal/toc? href]
    :as content}]
  (let [edit? (and edit? (not curated))
        card-label (if large?
                     :span
                     :div.truncate.nowrap.text-size-10)
        thumbnail (or thumbnailLink imageLink)]
    (merge content
           {:json-ld (am/content-SD content {:sd/about-url (am/current-url)})}
           (if edit?
             {:on-delete (partial delete-content (assoc content
                                                   :redirect
                                                   (ru/get-route)))}
             {:href (or href
                     (app-route/details-href content))})
           (if notes-grid?
             {:media [note-label content]
              :media-height "100%"
              :height 104
              :center-vert? false}
             (if thumbnail {:image thumbnail
                            :image-fit "cover"
                            :label-lines 2
                            :label-size (if large? 13 11)
                            :wrap? true
                            :label title
                            :label-inverted true}
                           {:label title
                            :label-lines 2
                            :label-size (if large? 13 11)
                            :wrap? true
                            :media-divider? true
                            :icon (content-icon content)})))))

(v/defview expandable-content-category
  {:get-initial-state
   (fn [{{:keys [content collapse-empty?]} :props}]
     {:expanded? (boolean
                   (if collapse-empty?
                     (seq content)
                     true))})}
  [{:keys [add-href label content edit? editable? collection-name collapse-empty? expand-inline?]}]
  (let [{:keys [state]} (r/current-component)
        {:keys [expanded?]} @state]
    [:div.pad-h-3
     [:div.pad-h-2.pad-v-3.text-category.flex.items-center
      {:on-click #(swap! state update :expanded? not)
       :class " cursor-pointer hover-text-black-hover"}
      [:div.pad-right-3
       {:class (when (empty? content) "opacity-50")}
       (if expanded?
         shapes/chevron-up
         shapes/chevron-down)]
      label
      (utils/coll-count 20 content)]
     (when expanded?
       [card/card-grid
        (merge card/file-grid-props
               {:class
                "pad-bottom-3"
                :add-card-props
                (when editable?
                  {:href add-href
                   :label (i/get (keyword "i" (str "add-" collection-name)))})})
        (->> content
             (mapv #(-> (merge %
                               {:edit? (and editable? edit?)}
                               (when expand-inline?
                                 {:expanded-view (v/partial-props content-details
                                                                  {:editable? editable?})}))
                        (content-card))))])]))

(defn contents-by-category
  [{:keys [sections add-query label-prefix dividers?] :as props}]
  (into [:div]
        (cond->> (for [[label-key
                        collection-name
                        content] sections]
                   [expandable-content-category
                    (-> props
                        (merge {:key collection-name
                                :label (cond->> (i/get label-key)
                                                label-prefix (str label-prefix " "))
                                :content content
                                :collection-name collection-name
                                :add-href (routing/path-for :router/form-modal [:add/file-by-collection :collection collection-name]
                                                            :query add-query)}))])
                 dividers? (interpose [:div.modal-divider])
                 dividers? (cons [:div.modal-divider]))))

(defn user-contents-by-category
  [{:keys [contents add-query label-prefix dividers?] :as props}]
  (let [{:strs [image video document note receipt nameplate]} (group-by :category contents)]
    (->> {:sections [[:i/photos-videos "media" (concat nameplate image video)]
                     [:i/documents "documents" document]
                     [:i/receipts "receipts" receipt]
                     [:i/notes "notes" note]]}
         (merge props)
         contents-by-category)))

